import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Table, Button, Switch, Input, Space, Tag, Modal, message, Tooltip, Card, Select, Form, Collapse, Typography, Timeline, Empty, Statistic, Row, Col } from 'antd';
import { SearchOutlined, DeleteOutlined, UserAddOutlined, LockOutlined, EditOutlined, KeyOutlined, EyeOutlined, EyeInvisibleOutlined, HistoryOutlined, ClockCircleOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { handleListSuperAdmins, handleEnableDiableUser, handleDeleteUser, handleCreateUser, handleUpdateUser, handleCheckUsername } from 'store/users/thunks';
import { apis } from 'utils/lib/apis';
import { handleGetUserPermissions, handleUpdateUserPermissions, handleUpdateUser2FA } from 'store/permissions/thunks';
import { useAllUsers, useCurrentUser } from 'store/users/selectors';
import { useSocket } from 'store/chat/selectors';
import { sendGenericSocketNotification, SOCKET_NOTIFICATION_CODES } from 'utils/socketNotifications';
import { useAdminActivityLogger } from 'components/hooks/useAdminActivityLogger';
import { ACTIVITY_TYPES, ACTIVITY_MODULES } from 'utils/adminActivityLogger';
import { useFirebase } from 'containers/FirebaseContext';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import styled from 'styled-components';
import SpinLoader from 'components/Loader/SpinLoader';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const Container = styled.div`
    padding: 20px;
`;

const HeaderActions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const PermissionManagementModal = ({ visible, user, onClose, onSave }) => {
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const { socketConnection, reCreateSocket } = useSocket();
    const { logPermissionActivity } = useAdminActivityLogger();
    const isCurrentUserRoot = currentUser?.is_root === true;
    const [loading, setLoading] = useState(false);
    const [userPermissions, setUserPermissions] = useState({ permissions: {} });
    const [isRootUser, setIsRootUser] = useState(false);
    const [activeKeys, setActiveKeys] = useState([]);

    useEffect(() => {
        if (visible && user) {
            setIsRootUser(user.is_root === true);
            if (user.is_root === true) {
                getAllPermissions();
            } else {
                fetchUserPermissions();
            }
        }
    }, [visible, user]);

    const fetchUserPermissions = async () => {
        setLoading(true);
        try {
            const response = await dispatch(handleGetUserPermissions(user.id));
            if (response && response.success && response.data) {
                setUserPermissions({
                    permissions: response.data.permissions || {}
                });
            } else {
                setUserPermissions({ permissions: {} });
            }
        } catch (error) {
            setUserPermissions({ permissions: {} });
        } finally {
            setLoading(false);
        }
    };

    const getAllPermissionKeys = () => {
        const allKeys = [];
        permissionCategories.forEach(category => {
            category.permissions.forEach(permission => {
                allKeys.push(permission.key);
            });
        });
        return allKeys;
    };

    const getAllPermissions = () => {
        const allKeys = getAllPermissionKeys();
        const allAllowed = {};
        allKeys.forEach(key => {
            allAllowed[key] = 'allow';
        });
        setUserPermissions({ permissions: allAllowed });
    };

    const handlePermissionChange = (permissionKey, value) => {
        const newPermissions = { ...userPermissions.permissions };
        newPermissions[permissionKey] = value;

        const category = permissionCategories.find(cat => 
            cat.permissions.some(p => p.key === permissionKey)
        );

        if (category) {
            const viewPermission = category.permissions.find(p => p.key.endsWith('.view'));
            
            if (viewPermission && value === 'allow') {
                const permissionBase = permissionKey.split('.').slice(0, -1).join('.');
                const action = permissionKey.split('.').pop();
                
                if (['add', 'edit', 'delete'].includes(action)) {
                    const viewKey = `${permissionBase}.view`;
                    if (category.permissions.some(p => p.key === viewKey)) {
                        newPermissions[viewKey] = 'allow';
                    }
                }
            }
        }

        setUserPermissions(prev => ({
            ...prev,
            permissions: newPermissions
        }));
    };

    const handleRootUserToggle = (checked) => {
        setIsRootUser(checked);
        if (checked) {
            getAllPermissions();
        }
    };

    const sendPermissionsUpdateViaSocket = (userId, permissions) => {
        sendGenericSocketNotification(socketConnection, reCreateSocket, {
            userId: userId,
            notificationCode: SOCKET_NOTIFICATION_CODES.PERMISSIONS_UPDATED,
            payload: {
                userId: userId,
                permissions: permissions
            }
        });
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const permissionsToSave = isRootUser ? getAllPermissionKeys().reduce((acc, key) => {
                acc[key] = 'allow';
                return acc;
            }, {}) : (userPermissions.permissions || {});

            const response = await dispatch(handleUpdateUserPermissions(user.id, {
                permissions: permissionsToSave
            }));
            
            if (response && response.success) {
                sendPermissionsUpdateViaSocket(user.id, response.data.permissions || permissionsToSave);
                
                logPermissionActivity(
                    user.id,
                    `${user.firstName} ${user.lastName}`,
                    permissionsToSave
                );
                
                if (isCurrentUserRoot) {
                    const updateUserResponse = await dispatch(handleUpdateUser(user.id, {
                        ...user,
                        is_root: isRootUser
                    }, {}, null));

                    if (updateUserResponse && updateUserResponse.id) {
                        message.success('User permissions and root status updated successfully');
                        onSave();
                        onClose();
                    } else {
                        message.error('Permissions updated but failed to update root status');
                    }
                } else {
                    message.success('User permissions updated successfully');
                    onSave();
                    onClose();
                }
            } else {
                message.error('Failed to update user permissions');
            }
        } catch (error) {
            message.error('Failed to update user permissions');
        } finally {
            setLoading(false);
        }
    };

    const permissionCategories = [
        {
            title: 'Data Browser',
            permissions: [
                { key: 'data_browser.access', label: 'Access Data Browser' }
            ]
        },
        {
            title: 'Settings',
            permissions: [
                { key: 'settings.welcome_video', label: 'Welcome Video' },
                { key: 'settings.fun_video_teacher', label: 'Fun Video for Teacher' },
                { key: 'settings.fun_video_student', label: 'Fun Video for Student' },
                { key: 'settings.demo_video', label: 'Demo Video' }
            ]
        },
        {
            title: 'Edit Books',
            permissions: [
                { key: 'edit_books.view', label: 'View Books' },
                { key: 'edit_books.add', label: 'Add Books' },
                { key: 'edit_books.edit', label: 'Edit Books' },
                { key: 'edit_books.activate', label: 'Activate Books' }
            ]
        },
        {
            title: 'Book Tools',
            permissions: [
                { key: 'book_tools.assignment', label: 'Assignment' },
                { key: 'book_tools.units', label: 'Units' },
                { key: 'book_tools.show_answers', label: 'Show Answers' },
                { key: 'book_tools.teacher_edition', label: 'Teacher Edition' },
                { key: 'book_tools.hyperlinks', label: 'Hyperlinks' },
                { key: 'book_tools.lesson_plan', label: 'Lesson Plan' },
                { key: 'book_tools.sentence_starter', label: 'Sentence Starter' },
                { key: 'book_tools.extension_activities', label: 'Extension Activities' },
                { key: 'book_tools.super_lesson', label: 'Super Lesson' },
                { key: 'book_tools.differentiation', label: 'Differentiation' },
                { key: 'book_tools.quiz', label: 'Quiz' },
                { key: 'book_tools.unit_exam', label: 'Unit Exam' },
                { key: 'book_tools.workbook', label: 'Workbook' },
                { key: 'book_tools.assessment_book', label: 'Assessment Book' },
                { key: 'book_tools.assessment_book_link', label: 'Assessment Book Link' },
                { key: 'book_tools.rubrics', label: 'Rubrics' },
                { key: 'book_tools.teacher_strategies', label: 'Teacher Strategies' },
                { key: 'book_tools.interactions', label: 'Interactions' },
                { key: 'book_tools.vocabulary', label: 'Vocabulary' },
                { key: 'book_tools.description_tags', label: 'Description and Tags' },
                { key: 'book_tools.en_sp', label: 'En - Sp' },
                { key: 'book_tools.grammar_ai_activity', label: 'Grammar AI Activity' },
                { key: 'book_tools.vocab_ai_activity', label: 'Vocab. AI Activity' },
                { key: 'book_tools.slides', label: 'Slides' },
                { key: 'book_tools.article_levels', label: 'Article Levels' },
                { key: 'book_tools.graphic_organizers', label: 'Graphic Organizers' },
                { key: 'book_tools.pdf_uploader', label: 'PDF Uploader' },
                { key: 'book_tools.pacing_guide', label: 'Pacing Guide' },
                { key: 'book_tools.syllabus', label: 'Syllabus' },
                { key: 'book_tools.mi_loteria', label: '¡Mi lotería!' },
                { key: 'book_tools.1game', label: '1Game!' },
                { key: 'book_tools.1study', label: '1Study!' }
            ]
        },
        {
            title: 'Book Sections',
            permissions: [
                { key: 'book_sections.informate', label: 'Edit Informate' },
                { key: 'book_sections.resources', label: 'Edit Resources' },
                { key: 'book_sections.balcon', label: 'Edit Balcón Cultural' },
                { key: 'book_sections.conectandome', label: 'Edit Conectándome' },
                { key: 'book_sections.comunidad', label: 'Edit Comunidad' },
                { key: 'book_sections.stories', label: 'Edit Story' },
                { key: 'book_sections.1wayteacher', label: 'Edit 1WayTeacher' },
                { key: 'book_sections.ap', label: 'Edit AP' },
                { key: 'book_sections.ap_exams', label: 'Edit AP Exams' },
                { key: 'book_sections.exams', label: 'Edit Exams' },
                { key: 'book_sections.how_to_videos', label: 'How To Videos' },
                { key: 'book_sections.book_grouping', label: 'Book Grouping' },
                { key: 'book_sections.sentence_starter', label: 'Sentence Starter' },
                { key: 'book_sections.rubric', label: 'Rubric' }
            ]
        },
        {
            title: 'Global Search',
            permissions: [
                { key: 'global_search.access', label: 'Access Global Search' }
            ]
        },
        {
            title: 'Standard',
            permissions: [
                { key: 'standard.add', label: 'Add Standard' },
                { key: 'standard.edit', label: 'Edit Standard' },
                { key: 'standard.delete', label: 'Delete Standard' },
                { key: 'standard.view', label: 'View Standard' }
            ]
        },
        {
            title: 'Quotes',
            permissions: [
                { key: 'quotes.add', label: 'Add Quotes' },
                { key: 'quotes.edit', label: 'Edit Quotes' },
                { key: 'quotes.delete', label: 'Delete Quotes' },
                { key: 'quotes.view', label: 'View Quotes' }
            ]
        },
        {
            title: 'Teacher Support',
            permissions: [
                { key: 'teacher_support.access', label: 'Access Teacher Support' }
            ]
        },
        {
            title: 'User Reported Issue',
            permissions: [
                { key: 'user_reported_issue.access', label: 'Access User Reported Issues' }
            ]
        },
        {
            title: 'Grammar Drill',
            permissions: [
                { key: 'grammar_drill.view', label: 'View Grammar Drill' },
                { key: 'grammar_drill.add', label: 'Add Grammar Drill' },
                { key: 'grammar_drill.edit', label: 'Edit Grammar Drill' },
                { key: 'grammar_drill.delete', label: 'Delete Grammar Drill' },
                { key: 'grammar_drill.approve', label: 'Approve Grammar Drill' },
                { key: 'grammar_drill.generate_drill', label: 'Generate Grammar Drill Questions' }
            ]
        },
        {
            title: 'Integration Search',
            permissions: [
                { key: 'integration_search.access', label: 'Access Integration Search' }
            ]
        },
        {
            title: 'Game',
            permissions: [
                { key: 'game.tutorial', label: 'Tutorial' },
                { key: 'game.updates', label: 'Updates' },
                { key: 'game.gallery', label: 'Gallery' },
                { key: 'game.login_as_admin', label: 'Login as Admin' }
            ]
        },
        {
            title: 'Placement Exam',
            permissions: [
                { key: 'placement_exam.view', label: 'View Placement Exam' },
                { key: 'placement_exam.add', label: 'Add Placement Exam' },
                { key: 'placement_exam.edit', label: 'Edit Placement Exam' },
                { key: 'placement_exam.duplicate', label: 'Duplicate Placement Exam' },
                { key: 'placement_exam.delete', label: 'Delete Placement Exam' },
                { key: 'placement_exam.assignment_management', label: 'Placement Exam Assignment Management' },
                { key: 'placement_exam.rubrics_management', label: 'Placement Exam Rubrics Management' }
            ]
        },
        {
            title: 'Inappropriate',
            permissions: [
                { key: 'inappropriate.access', label: 'Access Inappropriate Words Management' }
            ]
        },
        {
            title: 'What\'s New',
            permissions: [
                { key: 'whats_new.access', label: 'Access What\'s New' }
            ]
        },
        {
            title: 'Image Gallery',
            permissions: [
                { key: 'image_gallery.access', label: 'Access Image Gallery' }
            ]
        },
        {
            title: 'Audio Gallery',
            permissions: [
                { key: 'audio_gallery.access', label: 'Access Audio Gallery' }
            ]
        },
        {
            title: 'Super Admin Management',
            permissions: [
                { key: 'super_admin_management.access', label: 'Access Super Admin Management' }
            ]
        }
    ];

    const getCategoryStatus = (category) => {
        const categoryPermissions = category.permissions.map(p => p.key);
        const allowedCount = categoryPermissions.filter(key => userPermissions.permissions[key] === 'allow').length;
        const deniedCount = categoryPermissions.filter(key => !userPermissions.permissions[key] || userPermissions.permissions[key] === 'deny').length;
        
        if (allowedCount === categoryPermissions.length) return 'allow';
        if (deniedCount === categoryPermissions.length) return 'deny';
        return 'partial';
    };

    const handleCategoryChange = (category, value) => {
        if (value === 'partial') {
            return;
        }
        
        const newPermissions = { ...userPermissions.permissions };
        category.permissions.forEach(permission => {
            if (value === 'deny') {
                delete newPermissions[permission.key];
            } else {
                newPermissions[permission.key] = value;
            }
        });
        setUserPermissions(prev => ({
            ...prev,
            permissions: newPermissions
        }));
    };

    return (
        <Modal
            title={
                <div style={{ fontSize: 18, fontWeight: 600, color: '#262626' }}>
                    Manage Permissions - {user?.firstName} {user?.lastName}
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1600}
            destroyOnHidden={true}
            footer={[
                <Button key="cancel" onClick={onClose} size="large" style={{ minWidth: 100 }}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave} size="large" style={{ minWidth: 150 }}>
                    Save Permissions
                </Button>
            ]}
            style={{ top: 20 }}
            bodyStyle={{ 
                maxHeight: 'calc(100vh - 200px)', 
                overflowY: 'auto',
                padding: '24px',
                backgroundColor: '#fafafa'
            }}
        >
            <SpinLoader dataloader={loading && visible} />
            {isCurrentUserRoot && (
                <div style={{ 
                    marginBottom: 20, 
                    padding: 16, 
                    backgroundColor: '#fff7e6', 
                    border: '1px solid #ffd591', 
                    borderRadius: 6,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isRootUser ? 8 : 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
                            <strong>Root User:</strong> Enable to grant all permissions automatically
                        </div>
                        <Switch
                            checked={isRootUser}
                            onChange={handleRootUserToggle}
                            checkedChildren="Root User"
                            unCheckedChildren="Regular User"
                        />
                    </div>
                    {isRootUser && (
                        <div style={{ marginTop: 8, fontSize: 13, color: '#8c8c8c', lineHeight: 1.5 }}>
                            Root user has all permissions enabled. Disable root user to manage individual permissions.
                        </div>
                    )}
                </div>
            )}
            <Collapse
                activeKey={activeKeys}
                onChange={(keys) => setActiveKeys(keys)}
                ghost
                style={{
                    backgroundColor: 'transparent'
                }}
            >
                {permissionCategories.map((category, categoryIndex) => {
                    const categoryStatus = getCategoryStatus(category);
                    return (
                        <Collapse.Panel
                            key={categoryIndex}
                            header={
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    width: '100%',
                                    paddingRight: 8
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ 
                                            fontSize: 15, 
                                            fontWeight: 600, 
                                            color: '#262626',
                                            letterSpacing: '0.3px'
                                        }}>
                                            {category.title}
                                        </span>
                                        <span style={{
                                            display: 'inline-block',
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: categoryStatus === 'allow' ? '#52c41a' : categoryStatus === 'deny' ? '#ff4d4f' : '#faad14',
                                            boxShadow: `0 0 0 2px ${categoryStatus === 'allow' ? '#d9f7be' : categoryStatus === 'deny' ? '#ffccc7' : '#fff7e6'}`
                                        }} />
                                    </div>
                                    <Select
                                        value={categoryStatus}
                                        onChange={(val) => handleCategoryChange(category, val)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ 
                                            width: 130,
                                            backgroundColor: categoryStatus === 'allow' ? '#f6ffed' : categoryStatus === 'deny' ? '#fff1f0' : '#fffbe6',
                                            borderColor: categoryStatus === 'allow' ? '#b7eb8f' : categoryStatus === 'deny' ? '#ffccc7' : '#ffe58f',
                                            color: categoryStatus === 'allow' ? '#389e0d' : categoryStatus === 'deny' ? '#cf1322' : '#d46b08',
                                            fontWeight: 500
                                        }}
                                        size="small"
                                        disabled={isRootUser}
                                    >
                                        <Select.Option value="deny" style={{ color: '#ff4d4f' }}>Full Deny</Select.Option>
                                        <Select.Option value="partial" style={{ color: '#faad14' }}>Partial</Select.Option>
                                        <Select.Option value="allow" style={{ color: '#52c41a' }}>Full Allow</Select.Option>
                                    </Select>
                                </div>
                            }
                            style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                border: '1px solid #e8e8e8',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                        >
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '10px',
                                padding: '8px 0'
                            }}>
                                {category.permissions.map((permission, index) => {
                                    const permissionValue = (userPermissions.permissions || {})[permission.key] || 'deny';
                                    return (
                                        <div 
                                            key={permission.key} 
                                            style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                padding: '10px 12px',
                                                backgroundColor: '#fafafa',
                                                borderRadius: '6px',
                                                border: '1px solid #e8e8e8',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f0f0f0';
                                                e.currentTarget.style.borderColor = '#d9d9d9';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fafafa';
                                                e.currentTarget.style.borderColor = '#e8e8e8';
                                            }}
                                        >
                                            <span style={{ 
                                                fontSize: 13, 
                                                flex: 1, 
                                                marginRight: 12,
                                                color: '#262626',
                                                fontWeight: 400,
                                                lineHeight: 1.5
                                            }}>
                                                {permission.label}
                                            </span>
                                            <Select
                                                value={permissionValue}
                                                onChange={(value) => handlePermissionChange(permission.key, value)}
                                                style={{ 
                                                    width: 100,
                                                    minWidth: 100
                                                }}
                                                size="small"
                                                disabled={isRootUser}
                                            >
                                                <Select.Option value="allow">Allow</Select.Option>
                                                <Select.Option value="deny">Deny</Select.Option>
                                            </Select>
                                        </div>
                                    );
                                })}
                            </div>
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        </Modal>
    );
};

const { Text, Title } = Typography;

const getActivityTypeColor = (type) => {
    const colorMap = {
        [ACTIVITY_TYPES.CREATE]: 'green',
        [ACTIVITY_TYPES.UPDATE]: 'blue',
        [ACTIVITY_TYPES.DELETE]: 'red',
        [ACTIVITY_TYPES.VIEW]: 'default',
        [ACTIVITY_TYPES.ACTIVATE]: 'cyan',
        [ACTIVITY_TYPES.DEACTIVATE]: 'orange',
        [ACTIVITY_TYPES.PERMISSION_CHANGE]: 'purple',
        [ACTIVITY_TYPES.UPLOAD]: 'geekblue',
        [ACTIVITY_TYPES.DOWNLOAD]: 'magenta'
    };
    return colorMap[type] || 'default';
};

const getLocationLabel = (path) => {
    if (!path) return 'Unknown';
    
    const pathToLabelMap = {
        "/dashboard": "Home",
        "/data-browsers": "Data Browser",
        "/global-admin-search": "Global Search",
        "/standard": "Standard",
        "/quotes": "Quotes",
        "/teacher_support": "Teacher support",
        "/feedback_reports": "User Reported Issues",
        "/worksheet": "Grammar Drills",
        "/schoology": "Schoology",
        "/classlink": "Classlink",
        "/clever": "Clever",
        "/canvas": "Canvas",
        "/google": "Google",
        "/editBooks": "Edit Books",
        "/edit-1game-tutorial": "1Game - Tutorials",
        "/edit-1game-update": "1Game - Update",
        "/edit-1game-video": "1Game - Video",
        "/edit-1game-gallery": "1Game - Gallery",
        "/login_as_admin": "1Game - Login As Admin",
        "/placement_exam": "Placement exam",
        "/vocab_cogantes": "Inappropriate - Vocab cognates",
        "/inappropriate_words": "Inappropriate - Words",
        "/inappropriate_words_logs": "Inappropriate - Logs",
        "/whatsNew": "What's New",
        "/image_gallery": "Image gallery",
        "/audio_gallery": "Audio gallery",
        "/super-admin-control-panel": "Super Admin Control Panel",
        "/settings": "Settings"
    };
    
    return pathToLabelMap[path] || path;
};

const ActivityModal = ({ visible, user, onClose }) => {
    const { firestore } = useFirebase();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [days, setDays] = useState(7);

    useEffect(() => {
        if (visible && user && firestore) {
            fetchActivities();
        } else {
            setActivities([]);
            setStats(null);
        }
    }, [visible, user, firestore, days]);

    const fetchActivities = async () => {
        setLoading(true);
        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);

            const q = query(
                collection(firestore, 'admin_activities'),
                where('userId', '==', user.id),
                where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
                orderBy('timestamp', 'desc'),
                limit(100)
            );

            const querySnapshot = await getDocs(q);
            const activitiesData = [];

            querySnapshot.forEach((doc) => {
                activitiesData.push({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate()
                });
            });

            setActivities(activitiesData);
            calculateStats(activitiesData);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            message.error('Failed to load activity data');
        } finally {
            setLoading(false);
        }
    };

    const getActivityStatus = (lastActivity) => {
        if (!lastActivity || !lastActivity.timestamp) {
            return { status: 'inactive', color: '#8c8c8c', text: 'No Activity' };
        }

        const now = new Date();
        const lastActivityTime = new Date(lastActivity.timestamp);
        const minutesDiff = Math.floor((now - lastActivityTime) / (1000 * 60));

        // Check if it's a logout action
        if (lastActivity.action === 'user_logout' || lastActivity.activityType === 'logout') {
            return { status: 'logged_out', color: '#ff4d4f', text: 'Logged Out' };
        }

        // Active: less than 5 minutes
        if (minutesDiff < 5) {
            return { status: 'active', color: '#52c41a', text: 'Active' };
        }

        // Idle: 5 to 30 minutes
        if (minutesDiff < 30) {
            return { status: 'idle', color: '#faad14', text: 'Idle' };
        }

        // Inactive: more than 30 minutes
        return { status: 'inactive', color: '#8c8c8c', text: 'Inactive' };
    };

    const calculateStats = (activitiesData) => {
        const lastActivity = activitiesData[0] || null;
        const rawLocation = lastActivity?.details?.pathname || lastActivity?.resourceId || null;
        const currentLocation = getLocationLabel(rawLocation);

        const stats = {
            total: activitiesData.length,
            byType: {},
            byModule: {},
            lastActivity: lastActivity,
            currentLocation: currentLocation
        };

        activitiesData.forEach(activity => {
            stats.byType[activity.activityType] = (stats.byType[activity.activityType] || 0) + 1;
            stats.byModule[activity.module] = (stats.byModule[activity.module] || 0) + 1;
        });

        setStats(stats);
    };

    const activityColumns = [
        {
            title: 'Time',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp) => (
                <div>
                    <div>{dayjs(timestamp).format('MMM DD, YYYY')}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                        {dayjs(timestamp).format('HH:mm:ss')}
                    </div>
                    <div style={{ fontSize: '11px', color: '#bbb' }}>
                        {dayjs(timestamp).fromNow()}
                    </div>
                </div>
            )
        },
        {
            title: 'Type',
            dataIndex: 'activityType',
            key: 'activityType',
            width: 100,
            render: (type) => (
                <Tag color={getActivityTypeColor(type)}>
                    {type.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Module',
            dataIndex: 'module',
            key: 'module',
            width: 150
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 200
        },
        {
            title: 'Resource',
            dataIndex: 'resourceName',
            key: 'resourceName',
            render: (name, record) => (
                <div>
                    <div>{name || 'N/A'}</div>
                    {record.resourceId && (
                        <div style={{ fontSize: '11px', color: '#999' }}>
                            ID: {record.resourceId}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            render: (details) => {
                if (!details || Object.keys(details).length === 0) return 'N/A';
                return (
                    <pre style={{ 
                        margin: 0, 
                        fontSize: '10px', 
                        maxWidth: '250px', 
                        maxHeight: '100px',
                        overflow: 'auto',
                        backgroundColor: '#f5f5f5',
                        padding: '4px',
                        borderRadius: '4px'
                    }}>
                        {JSON.stringify(details, null, 2)}
                    </pre>
                );
            }
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <HistoryOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                    <span>Activity History - {user?.firstName} {user?.lastName}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1400}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
            style={{ top: 20 }}
            bodyStyle={{ 
                maxHeight: 'calc(100vh - 200px)', 
                overflowY: 'auto',
                padding: '24px'
            }}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <SpinLoader dataloader={true} />
                    <div style={{ marginTop: 16, color: '#666' }}>Loading activity data...</div>
                </div>
            ) : (
                <>
                    <div style={{ marginBottom: 24 }}>
                        <Space style={{ marginBottom: 16 }}>
                            <Select
                                value={days}
                                onChange={setDays}
                                style={{ width: 150 }}
                            >
                                <Select.Option value={1}>Last 24 hours</Select.Option>
                                <Select.Option value={3}>Last 3 days</Select.Option>
                                <Select.Option value={7}>Last 7 days</Select.Option>
                            </Select>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchActivities()}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>

                        <Row gutter={16} style={{ marginTop: 16 }}>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Total Activities"
                                        value={stats?.total || 0}
                                        prefix={<FileTextOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Most Active Module"
                                        value={
                                            stats?.byModule && Object.keys(stats.byModule).length > 0
                                                ? Object.entries(stats.byModule).sort((a, b) => b[1] - a[1])[0][0]
                                                : 'N/A'
                                        }
                                        valueStyle={{ fontSize: '14px' }}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Last Activity</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <ClockCircleOutlined style={{ fontSize: 20 }} />
                                        <div>
                                            <div style={{ fontSize: '16px', fontWeight: 500 }}>
                                                {stats?.lastActivity
                                                    ? dayjs(stats.lastActivity.timestamp).fromNow()
                                                    : 'N/A'}
                                            </div>
                                            {stats?.lastActivity && (
                                                <Tag 
                                                    color={getActivityStatus(stats.lastActivity).color}
                                                    style={{ marginTop: 4 }}
                                                >
                                                    {getActivityStatus(stats.lastActivity).text}
                                                </Tag>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Current Location"
                                        value={stats?.currentLocation || 'Unknown'}
                                        valueStyle={{ fontSize: '14px', wordBreak: 'break-all' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>

                    {activities.length === 0 ? (
                        <Empty
                            description={`No activity found for the last ${days} day${days > 1 ? 's' : ''}`}
                            style={{ padding: '40px 0' }}
                        />
                    ) : (
                        <>
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col span={12}>
                                    <Card 
                                        title="Activity by Type" 
                                        size="small"
                                        bodyStyle={{ maxHeight: '300px', overflowY: 'auto' }}
                                    >
                                        {stats?.byType && Object.keys(stats.byType).length > 0 ? (
                                            <Timeline>
                                                {Object.entries(stats.byType)
                                                    .sort((a, b) => b[1] - a[1])
                                                    .map(([type, count]) => (
                                                        <Timeline.Item
                                                            key={type}
                                                            color={getActivityTypeColor(type)}
                                                        >
                                                            <strong>{type.toUpperCase()}</strong>: {count} activities
                                                        </Timeline.Item>
                                                    ))}
                                            </Timeline>
                                        ) : (
                                            <Empty description="No activity data" />
                                        )}
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card 
                                        title="Activity by Module" 
                                        size="small"
                                        bodyStyle={{ maxHeight: '300px', overflowY: 'auto' }}
                                    >
                                        {stats?.byModule && Object.keys(stats.byModule).length > 0 ? (
                                            <Timeline>
                                                {Object.entries(stats.byModule)
                                                    .sort((a, b) => b[1] - a[1])
                                                    .slice(0, 10)
                                                    .map(([module, count]) => (
                                                        <Timeline.Item key={module}>
                                                            <strong>{module}</strong>: {count} activities
                                                        </Timeline.Item>
                                                    ))}
                                            </Timeline>
                                        ) : (
                                            <Empty description="No activity data" />
                                        )}
                                    </Card>
                                </Col>
                            </Row>

                            <Card title="Detailed Activity Log" size="small">
                                <Table
                                    columns={activityColumns}
                                    dataSource={activities}
                                    rowKey="id"
                                    size="small"
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: true,
                                        showTotal: (total) => `Total ${total} activities`
                                    }}
                                    scroll={{ x: 1200 }}
                                />
                            </Card>
                        </>
                    )}
                </>
            )}
        </Modal>
    );
};

const ViewPasswordModal = ({ visible, user, onClose }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && user) {
            fetchUserPassword();
        } else {
            setPassword('');
            setShowPassword(false);
            setError(null);
        }
    }, [visible, user]);

    const fetchUserPassword = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apis.post('usersRDS', `/get_user_password`, {
                body: {
                    id: user.id,
                    username: user.username
                }
            });

            if (response && response.success && response.data && response.data.password) {
                try {
                    const decodedPassword = atob(response.data.password);
                    setPassword(decodedPassword);
                } catch (decodeError) {
                    setError('Failed to decode password');
                    setPassword('');
                }
            } else {
                setError('Password not found for this user');
            }
        } catch (error) {
            setError('Failed to fetch password');
            console.error('Error fetching password:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPassword = () => {
        if (password) {
            navigator.clipboard.writeText(password);
            message.success('Password copied to clipboard');
        }
    };

    return (
        <Modal
            title={`View Password - ${user?.firstName} ${user?.lastName}`}
            open={visible}
            onCancel={onClose}
            width={500}
            destroyOnHidden={true}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
        >
            <div style={{ padding: '20px 0' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <SpinLoader dataloader={true} />
                        <div style={{ marginTop: 16, color: '#666' }}>Loading password...</div>
                    </div>
                ) : error ? (
                    <div style={{ 
                        padding: '20px', 
                        backgroundColor: '#fff1f0', 
                        border: '1px solid #ffccc7', 
                        borderRadius: 4,
                        color: '#cf1322',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Password:</Text>
                        </div>
                        <Input.Group compact style={{ display: 'flex', marginBottom: 16 }}>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                readOnly
                                style={{ flex: 1 }}
                                placeholder="Password will appear here"
                            />
                            <Button
                                icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ width: 50 }}
                            />
                            <Button
                                onClick={handleCopyPassword}
                                disabled={!password}
                            >
                                Copy
                            </Button>
                        </Input.Group>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: '#fff7e6', 
                            border: '1px solid #ffe58f', 
                            borderRadius: 4,
                            fontSize: 12,
                            color: '#d46b08'
                        }}>
                            <strong>Security Notice:</strong> This password is displayed for administrative purposes only. Keep it secure and do not share it.
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

const ResetPasswordModal = ({ visible, user, onClose, onSave }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [formatPasswordError, setFormatPasswordError] = useState(false);

    useEffect(() => {
        if (visible) {
            setLoading(false);
            form.resetFields();
            setPasswordError(false);
            setFormatPasswordError(false);
        }
    }, [visible]);

    const passwordValidation = (password) => {
        if (!password) {
            setPasswordError(true);
            return false;
        }
        const paswd = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/;
        if (!password.match(paswd)) {
            setFormatPasswordError(true);
            return false;
        }
        setPasswordError(false);
        setFormatPasswordError(false);
        return true;
    };

    const handleSubmit = async (values) => {
        if (!values.password) {
            setPasswordError(true);
            return;
        }
        if (!passwordValidation(values.password)) {
            return;
        }

        setLoading(true);
        try {
            const response = await apis.put('usersRDS', `/updatePassword`, {
                body: {
                    username: user.username,
                    password: values.password
                }
            });

            if (response && !response.error) {
                setLoading(false);
                message.success('Password reset successfully');
                onSave();
                onClose();
            } else {
                message.error(response?.error || 'Failed to reset password');
                setLoading(false);
            }
        } catch (error) {
            message.error('Failed to reset password');
            setLoading(false);
        }
    };

    return (
        <Modal
            title={`Reset Password - ${user?.firstName} ${user?.lastName}`}
            open={visible}
            onCancel={onClose}
            width={500}
            destroyOnHidden={true}
            footer={null}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 20 }}
            >
                <Form.Item
                    name="password"
                    label="New Password"
                    rules={[{ required: true, message: 'Password is required' }]}
                    validateStatus={passwordError || formatPasswordError ? 'error' : ''}
                    help={
                        passwordError 
                            ? 'Password is required' 
                            : formatPasswordError 
                                ? 'Use 8 to 15 characters which contain at least one lowercase letter, one uppercase letter, one numeric digit and one special character.'
                                : ''
                    }
                >
                    <Input.Password 
                        placeholder="Enter new password"
                        onChange={(e) => passwordValidation(e.target.value)}
                    />
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    dependencies={['password']}
                    rules={[
                        { required: true, message: 'Please confirm your password' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('The two passwords do not match'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Confirm new password" />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Reset Password
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const EditProfileModal = ({ visible, user, onClose, onSave, isCreating }) => {
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const isCurrentUserRoot = currentUser?.is_root === true;
    const isEditingSelf = !isCreating && currentUser?.id === user?.id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [usernameError, setUsernameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [formatPasswordError, setFormatPasswordError] = useState(false);

    useEffect(() => {
        if (visible) {
            setLoading(false);
            if (isCreating) {
                form.resetFields();
                form.setFieldsValue({
                    role: 'owe_admin',
                    active: true
                });
            } else if (user) {
                form.resetFields();
                form.setFieldsValue({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username,
                    email: user.email || '',
                    active: user.active !== undefined ? user.active : true,
                    is_root: user.is_root === true
                });
            }
        } else {
            setLoading(false);
            setUsernameError(false);
            setPasswordError(false);
            setFormatPasswordError(false);
        }
    }, [visible, user, isCreating]);

    const handleUsernameCheck = async (username) => {
        if (!username) {
            setUsernameError(false);
            return false;
        }
        if (isCreating) {
            const exists = await dispatch(handleCheckUsername(username));
            if (exists) {
                setUsernameError(true);
                return true;
            }
        }
        setUsernameError(false);
        return false;
    };

    const passwordValidation = (password) => {
        if (!password && isCreating) {
            setPasswordError(true);
            return false;
        }
        if (password) {
            const paswd = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/;
            if (!password.match(paswd)) {
                setFormatPasswordError(true);
                return false;
            }
        }
        setPasswordError(false);
        setFormatPasswordError(false);
        return true;
    };

    const handleSubmit = async (values) => {
        if (isCreating) {
            if (!values.password) {
                setPasswordError(true);
                return;
            }
            if (!passwordValidation(values.password)) {
                return;
            }
            const usernameExists = await handleUsernameCheck(values.username);
            if (usernameExists) {
                message.error('Username already exists');
                return;
            }
        }

        setLoading(true);
        try {
            if (isCreating) {
                const newUser = await dispatch(handleCreateUser({
                    auth: {
                        username: values.username,
                        password: values.password,
                    },
                    user: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        role: 'owe_admin',
                        email: values.email || null,
                        active: values.active !== undefined ? values.active : true,
                    },
                    userBooks: {}
                }));

                if (newUser && !newUser.error && newUser.id) {
                    setLoading(false);
                    message.success('Super admin created successfully');
                    onSave();
                    onClose();
                } else {
                    message.error(newUser?.message || 'Failed to create super admin');
                    setLoading(false);
                }
            } else {
                const updatedUser = await dispatch(handleUpdateUser(user.id, {
                    ...user,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email || null,
                    active: values.active !== undefined ? values.active : true,
                    is_root: isCurrentUserRoot ? (values.is_root === true) : user.is_root
                }, {}, null));

                if (updatedUser && updatedUser.id) {
                    if (isCurrentUserRoot && values.is_root) {
                        const permissionCategories = [
                            { permissions: [{ key: 'data_browser.access' }] },
                            { permissions: [{ key: 'settings.welcome_video' }, { key: 'settings.fun_video_teacher' }, { key: 'settings.fun_video_student' }, { key: 'settings.demo_video' }] },
                            { permissions: [{ key: 'edit_books.view' }, { key: 'edit_books.add' }, { key: 'edit_books.edit' }, { key: 'edit_books.activate' }] },
                            { permissions: [{ key: 'book_sections.informate' }, { key: 'book_sections.resources' }, { key: 'book_sections.balcon' }, { key: 'book_sections.conectandome' }, { key: 'book_sections.comunidad' }, { key: 'book_sections.stories' }, { key: 'book_sections.1wayteacher' }, { key: 'book_sections.ap' }, { key: 'book_sections.ap_exams' }, { key: 'book_sections.exams' }, { key: 'book_sections.how_to_videos' }, { key: 'book_sections.book_grouping' }, { key: 'book_sections.sentence_starter' }, { key: 'book_sections.rubric' }] },
                            { permissions: [{ key: 'book_tools.assignment' }, { key: 'book_tools.units' }, { key: 'book_tools.show_answers' }, { key: 'book_tools.teacher_edition' }, { key: 'book_tools.hyperlinks' }, { key: 'book_tools.lesson_plan' }, { key: 'book_tools.sentence_starter' }, { key: 'book_tools.extension_activities' }, { key: 'book_tools.super_lesson' }, { key: 'book_tools.differentiation' }, { key: 'book_tools.quiz' }, { key: 'book_tools.unit_exam' }, { key: 'book_tools.workbook' }, { key: 'book_tools.assessment_book' }, { key: 'book_tools.assessment_book_link' }, { key: 'book_tools.rubrics' }, { key: 'book_tools.teacher_strategies' }, { key: 'book_tools.interactions' }, { key: 'book_tools.vocabulary' }, { key: 'book_tools.description_tags' }, { key: 'book_tools.en_sp' }, { key: 'book_tools.grammar_ai_activity' }, { key: 'book_tools.vocab_ai_activity' }, { key: 'book_tools.slides' }, { key: 'book_tools.article_levels' }, { key: 'book_tools.graphic_organizers' }, { key: 'book_tools.pdf_uploader' }, { key: 'book_tools.pacing_guide' }, { key: 'book_tools.syllabus' }, { key: 'book_tools.mi_loteria' }, { key: 'book_tools.1game' }, { key: 'book_tools.1study' }] },
                            { permissions: [{ key: 'global_search.access' }] },
                            { permissions: [{ key: 'standard.add' }, { key: 'standard.edit' }, { key: 'standard.delete' }, { key: 'standard.view' }] },
                            { permissions: [{ key: 'quotes.add' }, { key: 'quotes.edit' }, { key: 'quotes.delete' }, { key: 'quotes.view' }] },
                            { permissions: [{ key: 'teacher_support.access' }] },
                            { permissions: [{ key: 'user_reported_issue.access' }] },
                            { permissions: [{ key: 'grammar_drill.view' }, { key: 'grammar_drill.add' }, { key: 'grammar_drill.edit' }, { key: 'grammar_drill.delete' }, { key: 'grammar_drill.approve' }] },
                            { permissions: [{ key: 'integration_search.access' }] },
                            { permissions: [{ key: 'game.tutorial' }, { key: 'game.updates' }, { key: 'game.gallery' }, { key: 'game.login_as_admin' }] },
                            { permissions: [{ key: 'placement_exam.full_access' }] },
                            { permissions: [{ key: 'exam.add' }, { key: 'exam.edit' }, { key: 'exam.duplicate' }, { key: 'exam.delete' }] },
                            { permissions: [{ key: 'assignment_management.access' }] },
                            { permissions: [{ key: 'placement_exam_rubrics.add' }, { key: 'placement_exam_rubrics.edit' }, { key: 'placement_exam_rubrics.delete' }] },
                            { permissions: [{ key: 'inappropriate.word.add' }, { key: 'inappropriate.word.edit' }, { key: 'inappropriate.word.delete' }, { key: 'inappropriate.logs' }] },
                            { permissions: [{ key: 'whats_new.access' }] },
                            { permissions: [{ key: 'image_gallery.access' }] },
                            { permissions: [{ key: 'audio_gallery.access' }] },
                            { permissions: [{ key: 'super_admin_management.access' }] }
                        ];
                        const allKeys = [];
                        permissionCategories.forEach(category => {
                            category.permissions.forEach(permission => {
                                allKeys.push(permission.key);
                            });
                        });
                        const allAllowed = {};
                        allKeys.forEach(key => {
                            allAllowed[key] = 'allow';
                        });
                        await dispatch(handleUpdateUserPermissions(user.id, {
                            permissions: allAllowed
                        }));
                    }
                    setLoading(false);
                    message.success('Super admin updated successfully');
                    onSave();
                    onClose();
                } else {
                    message.error('Failed to update super admin');
                    setLoading(false);
                }
            }
        } catch (error) {
            message.error(`Failed to ${isCreating ? 'create' : 'update'} super admin`);
            setLoading(false);
        }
    };
    return (
        <Modal
            title={isCreating ? 'Add Super Admin' : `Edit Profile - ${user?.firstName} ${user?.lastName}`}
            open={visible}
            onCancel={onClose}
            width={600}
            destroyOnHidden={true}
            footer={null}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 20 }}
            >
                <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'First name is required' }]}
                >
                    <Input placeholder="Enter first name" />
                </Form.Item>

                <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Last name is required' }]}
                >
                    <Input placeholder="Enter last name" />
                </Form.Item>

                <Form.Item
                    name="username"
                    label="Username"
                    rules={[{ required: true, message: 'Username is required' }]}
                    validateStatus={usernameError ? 'error' : ''}
                    help={usernameError ? 'Username already exists' : ''}
                >
                    <Input 
                        placeholder="Enter username" 
                        disabled={!isCreating}
                        onBlur={(e) => {
                            if (e.target.value && isCreating) {
                                handleUsernameCheck(e.target.value);
                            }
                        }}
                    />
                </Form.Item>

                {isCreating && (
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Password is required' }]}
                        validateStatus={passwordError || formatPasswordError ? 'error' : ''}
                        help={
                            passwordError 
                                ? 'Password is required' 
                                : formatPasswordError 
                                    ? 'Use 8 to 15 characters which contain at least one lowercase letter, one uppercase letter, one numeric digit and one special character.'
                                    : ''
                        }
                    >
                        <Input.Password 
                            placeholder="Enter password"
                            onChange={(e) => passwordValidation(e.target.value)}
                        />
                    </Form.Item>
                )}

                <Form.Item
                    name="email"
                    label="Email"
                >
                    <Input type="email" placeholder="Enter email (required for 2FA)" />
                </Form.Item>

                <Form.Item
                    name="active"
                    label="Status"
                    valuePropName="checked"
                >
                    <Tooltip title={isEditingSelf ? "You cannot deactivate your own account" : ""}>
                        <Switch 
                            checkedChildren="Active" 
                            unCheckedChildren="Inactive"
                             checked={user?.active}
                            disabled={isEditingSelf}
                        />
                    </Tooltip>
                </Form.Item>
                {isEditingSelf && (
                    <div style={{ marginBottom: 16, padding: 8, backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, fontSize: 12, color: '#666' }}>
                        You cannot deactivate your own account. Ask another root user to deactivate it if needed.
                    </div>
                )}

                {isCurrentUserRoot && (
                    <Form.Item
                        name="is_root"
                        label="Root User"
                        valuePropName="checked"
                        tooltip="Root users have all permissions enabled automatically. Only root users can change this setting."
                    >
                        <Switch checkedChildren="Root User" unCheckedChildren="Regular User" />
                    </Form.Item>
                )}

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {isCreating ? 'Create' : 'Update'}
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const UsersManagement = () => {
    const dispatch = useDispatch();
    const allUsers = useAllUsers();
    const currentUser = useCurrentUser();
    const { logUserActivity } = useAdminActivityLogger();
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [enableLoading, setEnableLoading] = useState({});
    const [twoFactorUpdating, setTwoFactorUpdating] = useState({});
    const [twoFactorStatus, setTwoFactorStatus] = useState({});
    const [permissionModalVisible, setPermissionModalVisible] = useState(false);
    const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
    const [selectedUserForResetPassword, setSelectedUserForResetPassword] = useState(null);
    const [viewPasswordModalVisible, setViewPasswordModalVisible] = useState(false);
    const [selectedUserForViewPassword, setSelectedUserForViewPassword] = useState(null);
    const [activityModalVisible, setActivityModalVisible] = useState(false);
    const [selectedUserForActivity, setSelectedUserForActivity] = useState(null);
    const [userLastActivities, setUserLastActivities] = useState({});
    const { firestore } = useFirebase();

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (allUsers && allUsers.length > 0 && firestore) {
            fetchLastActivities();
        }
    }, [allUsers, firestore]);

    useEffect(() => {
        if (allUsers && allUsers.length > 0) {
            const statusMap = {};
            allUsers.forEach(user => {
                statusMap[user.id] = user.two_factor_enabled || false;
            });
            setTwoFactorStatus(statusMap);
        }
    }, [allUsers]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            await dispatch(handleListSuperAdmins());
        } catch (error) {
            message.error('Failed to fetch super admins');
        } finally {
            setLoading(false);
        }
    };

    const fetchLastActivities = async () => {
        if (!firestore || !allUsers || allUsers.length === 0) return;
        
        try {
            const activitiesMap = {};
            const promises = allUsers.map(async (user) => {
                try {
                    const q = query(
                        collection(firestore, 'admin_activities'),
                        where('userId', '==', user.id),
                        orderBy('timestamp', 'desc'),
                        limit(1)
                    );
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        const doc = querySnapshot.docs[0];
                        const activityData = doc.data();
                        activitiesMap[user.id] = {
                            timestamp: activityData.timestamp?.toDate(),
                            action: activityData.action,
                            activityType: activityData.activityType
                        };
                    }
                } catch (error) {
                    // Silently fail for individual users
                    console.error(`Failed to fetch last activity for user ${user.id}:`, error);
                }
            });
            
            await Promise.all(promises);
            setUserLastActivities(activitiesMap);
        } catch (error) {
            console.error('Failed to fetch last activities:', error);
        }
    };

    const handleToggle2FA = async (userId, enabled, userEmail) => {
        if (enabled && !userEmail) {
            message.warning('2FA can only be enabled for users with an email address');
            return;
        }
        
        const targetUser = allUsers?.find(u => u.id === userId);
        const isRootUser = targetUser?.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can enable/disable 2FA for root users.');
            return;
        }
        
        setTwoFactorUpdating(prev => ({ ...prev, [userId]: true }));
        try {
            const response = await dispatch(handleUpdateUser2FA(userId, enabled));
            if (response && response.success) {
                setTwoFactorStatus(prev => ({
                    ...prev,
                    [userId]: enabled
                }));
                message.success(`2FA ${enabled ? 'enabled' : 'disabled'} successfully`);
            } else {
                message.error('Failed to update 2FA status');
            }
        } catch (error) {
            message.error('Failed to update 2FA status');
        } finally {
            setTwoFactorUpdating(prev => ({ ...prev, [userId]: false }));
        }
    };

    const handleManagePermissions = (user) => {
        const isRootUser = user.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can manage root user permissions.');
            return;
        }
        
        setSelectedUserForPermissions(user);
        setPermissionModalVisible(true);
    };

    const handleEditProfile = (user) => {
        const isRootUser = user?.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can edit root users.');
            return;
        }
        
        setSelectedUserForEdit(user);
        setIsCreating(false);
        setEditModalVisible(true);
    };

    const handleAddSuperAdmin = () => {
        if (!currentUser?.is_root) {
            message.warning('Only root users can add new super admins');
            return;
        }
        setSelectedUserForEdit(null);
        setIsCreating(true);
        setEditModalVisible(true);
    };

    const handleResetPassword = (user) => {
        const isRootUser = user.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (!canModifyRoot) {
            message.warning('Only root users can reset passwords');
            return;
        }
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can reset passwords for root users.');
            return;
        }
        
        setSelectedUserForResetPassword(user);
        setResetPasswordModalVisible(true);
    };

    const handleToggleActive = async (userId, currentStatus) => {
        const isCurrentUser = currentUser?.id === userId;
        const targetUser = allUsers?.find(u => u.id === userId);
        const isRootUser = targetUser?.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (isCurrentUser && currentStatus) {
            message.warning('You cannot deactivate your own account. Ask another root user to deactivate it if needed.');
            return;
        }
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can modify root users.');
            return;
        }
        
        setEnableLoading(prev => ({ ...prev, [userId]: true }));
        try {
            await dispatch(handleEnableDiableUser(userId, !currentStatus));
            message.success(`Super admin ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
            await fetchUsers();
        } catch (error) {
            message.error('Failed to update super admin status');
        } finally {
            setEnableLoading(prev => ({ ...prev, [userId]: false }));
        }
    };

    const handleDelete = (user) => {
        const isRootUser = user.is_root === true;
        const canModifyRoot = currentUser?.is_root === true;
        
        if (isRootUser && !canModifyRoot) {
            message.warning('Only root users can delete root users.');
            return;
        }
        
        Modal.confirm({
            title: 'Delete Super Admin',
            content: `Are you sure you want to delete super admin ${user.firstName} ${user.lastName}? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await dispatch(handleDeleteUser(user.id, user.username));
                    message.success('Super admin deleted successfully');
                    await fetchUsers();
                } catch (error) {
                    message.error('Failed to delete super admin');
                }
            }
        });
    };

    const filteredUsers = useMemo(() => {
        const superAdmins = allUsers || [];
        if (!searchText) return superAdmins;
        const search = searchText.toLowerCase();
        return superAdmins.filter(user => 
            `${user.firstName} ${user.lastName}`.toLowerCase().includes(search) ||
            user.username?.toLowerCase().includes(search) ||
            user.email?.toLowerCase().includes(search) ||
            user.user_sub_roles?.toLowerCase().includes(search)
        );
    }, [allUsers, searchText]);

    const rootUsers = useMemo(() => {
        return filteredUsers
            .filter(user => user.is_root === true)
            .sort((a, b) => {
                // Sort by last active time (most recent first)
                const aLastActive = userLastActivities[a.id]?.timestamp;
                const bLastActive = userLastActivities[b.id]?.timestamp;
                
                if (aLastActive && bLastActive) {
                    return bLastActive.getTime() - aLastActive.getTime();
                }
                if (aLastActive && !bLastActive) return -1;
                if (!aLastActive && bLastActive) return 1;
                
                // If no last active, sort by active status
                if (a.active === b.active) return 0;
                return a.active ? -1 : 1;
            });
    }, [filteredUsers, userLastActivities]);

    const regularUsers = useMemo(() => {
        return filteredUsers
            .filter(user => !user.is_root || user.is_root !== true)
            .sort((a, b) => {
                // Sort by last active time (most recent first)
                const aLastActive = userLastActivities[a.id]?.timestamp;
                const bLastActive = userLastActivities[b.id]?.timestamp;
                
                if (aLastActive && bLastActive) {
                    return bLastActive.getTime() - aLastActive.getTime();
                }
                if (aLastActive && !bLastActive) return -1;
                if (!aLastActive && bLastActive) return 1;
                
                // If no last active, sort by active status
                if (a.active === b.active) return 0;
                return a.active ? -1 : 1;
            });
    }, [filteredUsers, userLastActivities]);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'firstName',
            key: 'name',
            render: (text, record) => (
                <span>
                    {`${record.firstName} ${record.lastName}`}
                    {record.is_root && (
                        <Tag color="gold" style={{ marginLeft: 8 }}>Root</Tag>
                    )}
                </span>
            ),
            sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            sorter: (a, b) => (a.username || '').localeCompare(b.username || '')
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => (a.email || '').localeCompare(b.email || '')
        },
        {
            title: 'Last Active',
            key: 'lastActive',
            width: 180,
            render: (_, record) => {
                const lastActivity = userLastActivities[record.id];
                if (!lastActivity || !lastActivity.timestamp) {
                    return <span style={{ color: '#8c8c8c' }}>Never</span>;
                }

                const now = new Date();
                const lastActivityTime = new Date(lastActivity.timestamp);
                const minutesDiff = Math.floor((now - lastActivityTime) / (1000 * 60));

                let statusColor = '#8c8c8c';
                let statusText = 'Inactive';

                // Check if it's a logout action
                if (lastActivity.action === 'user_logout' || lastActivity.activityType === 'logout') {
                    statusColor = '#ff4d4f';
                    statusText = 'Logged Out';
                } else if (minutesDiff < 5) {
                    statusColor = '#52c41a';
                    statusText = 'Active';
                } else if (minutesDiff < 30) {
                    statusColor = '#faad14';
                    statusText = 'Idle';
                }

                return (
                    <div>
                        <div style={{ fontSize: '13px' }}>
                            {dayjs(lastActivity.timestamp).format('MMM DD, YYYY')}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                            {dayjs(lastActivity.timestamp).format('HH:mm:ss')}
                        </div>
                        <div style={{ fontSize: '11px', color: '#bbb', marginTop: 2 }}>
                            {dayjs(lastActivity.timestamp).fromNow()}
                        </div>
                        <Tag 
                            color={statusColor} 
                            style={{ marginTop: 4, fontSize: '11px' }}
                        >
                            {statusText}
                        </Tag>
                    </div>
                );
            },
            sorter: (a, b) => {
                const aLastActive = userLastActivities[a.id]?.timestamp;
                const bLastActive = userLastActivities[b.id]?.timestamp;
                
                if (aLastActive && bLastActive) {
                    return bLastActive.getTime() - aLastActive.getTime();
                }
                if (aLastActive && !bLastActive) return -1;
                if (!aLastActive && bLastActive) return 1;
                return 0;
            },
            defaultSortOrder: 'descend'
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active, record) => {
                const isCurrentUser = currentUser?.id === record.id;
                const isRootUser = record.is_root === true;
                const canModifyRoot = currentUser?.is_root === true;
                const isDisabled = (isCurrentUser && active) || (isRootUser && !canModifyRoot);
                const tooltipText = isCurrentUser && active 
                    ? "You cannot deactivate your own account" 
                    : isRootUser && !canModifyRoot 
                        ? "Only root users can modify root users" 
                        : "";
                return (
                    <Tooltip title={tooltipText}>
                        <Switch
                            checked={active}
                            loading={enableLoading[record.id]}
                            onChange={() => handleToggleActive(record.id, active)}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            disabled={isDisabled}
                        />
                    </Tooltip>
                );
            }
        },
        {
            title: '2FA',
            key: '2fa',
            render: (_, record) => {
                const enabled = twoFactorStatus[record.id] || false;
                const hasEmail = record.email && record.email.trim() !== '';
                const isRootUser = record.is_root === true;
                const canModifyRoot = currentUser?.is_root === true;
                const isDisabled = (!hasEmail && !enabled) || (isRootUser && !canModifyRoot);
                const tooltipText = !hasEmail 
                    ? 'Email required to enable 2FA' 
                    : isRootUser && !canModifyRoot 
                        ? 'Only root users can enable/disable 2FA for root users' 
                        : '';
                return (
                    <Tooltip title={tooltipText}>
                        <Switch
                            checked={enabled}
                            loading={twoFactorUpdating[record.id]}
                            onChange={(checked) => handleToggle2FA(record.id, checked, record.email)}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            disabled={isDisabled}
                        />
                    </Tooltip>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const isRootUser = record.is_root === true;
                const canModifyRoot = currentUser?.is_root === true;
                const canEdit = !isRootUser || canModifyRoot;
                const canManagePermissions = !isRootUser || canModifyRoot;
                const canResetPassword = canModifyRoot;
                const canViewPassword = canModifyRoot;
                const canDelete = !isRootUser || canModifyRoot;
                
                return (
                    <Space size="middle">
                        <Tooltip title={canEdit ? "Edit Profile" : "Only root users can edit root users"}>
                            <Button
                                type="text"
                                icon={<EditOutlined style={{ fontSize: '18px', color: '#1890ff' }} />}
                                onClick={() => handleEditProfile(record)}
                                disabled={!canEdit}
                            />
                        </Tooltip>
                        <Tooltip title={
                            isRootUser 
                                ? (canModifyRoot ? "Manage Permissions" : "Only root users can manage root user permissions")
                                : "Manage Permissions"
                        }>
                            <Button
                                type="text"
                                icon={<LockOutlined style={{ fontSize: '18px', color: '#fa8c16' }} />}
                                onClick={() => handleManagePermissions(record)}
                                disabled={!canManagePermissions}
                            />
                        </Tooltip>
                        <Tooltip title={canResetPassword ? "Reset Password" : "Only root users can reset passwords"}>
                            <Button
                                type="text"
                                icon={<KeyOutlined style={{ fontSize: '18px', color: '#722ed1' }} />}
                                onClick={() => handleResetPassword(record)}
                                disabled={!canResetPassword}
                            />
                        </Tooltip>
                        <Tooltip title={canViewPassword ? "View Password" : "Only root users can view passwords"}>
                            <Button
                                type="text"
                                icon={<EyeOutlined style={{ fontSize: '18px', color: '#13c2c2' }} />}
                                onClick={() => {
                                    setSelectedUserForViewPassword(record);
                                    setViewPasswordModalVisible(true);
                                }}
                                disabled={!canViewPassword}
                            />
                        </Tooltip>
                        <Tooltip title="View Activity History">
                            <Button
                                type="text"
                                icon={<HistoryOutlined style={{ fontSize: '18px', color: '#722ed1' }} />}
                                onClick={() => {
                                    setSelectedUserForActivity(record);
                                    setActivityModalVisible(true);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={canDelete ? "Delete Super Admin" : "Only root users can delete root users"}>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined style={{ fontSize: '18px' }} />}
                                onClick={() => handleDelete(record)}
                                disabled={!canDelete}
                            />
                        </Tooltip>
                    </Space>
                );
            }
        }
    ];
    return (
        <Container>
            <SpinLoader dataloader={loading} />
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f2f5', borderRadius: 4 }}>
                <strong>Super Admin Management:</strong> Manage super admin users . Only root super admins can manage other super admins.
            </div>
            <HeaderActions>
                <Input
                    placeholder="Search super admins by name, username, email, or sub-role"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 400 }}
                    allowClear
                />
                <Tooltip title={!currentUser?.is_root ? "Only root users can add new super admins" : ""}>
                    <Button
                        type="primary"
                        icon={<UserAddOutlined />}
                        onClick={handleAddSuperAdmin}
                        disabled={!currentUser?.is_root}
                    >
                        Add Super Admin
                    </Button>
                </Tooltip>
            </HeaderActions>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, color: '#1890ff' }}>
                    Root Users ({rootUsers.length})
                </h2>
                <Table
                    columns={columns}
                    dataSource={rootUsers}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} root users`
                    }}
                    locale={{
                        emptyText: 'No root users found'
                    }}
                />
            </div>

            <div>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, color: '#52c41a' }}>
                    Regular Users ({regularUsers.length})
                </h2>
                <Table
                    columns={columns}
                    dataSource={regularUsers}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} regular users`
                    }}
                    locale={{
                        emptyText: 'No regular users found'
                    }}
                />
            </div>
            <PermissionManagementModal
                visible={permissionModalVisible}
                user={selectedUserForPermissions}
                onClose={() => {
                    setPermissionModalVisible(false);
                    setSelectedUserForPermissions(null);
                }}
                onSave={() => {
                    fetchUsers();
                }}
            />
                <EditProfileModal
                    visible={editModalVisible}
                    user={selectedUserForEdit}
                    isCreating={isCreating}
                    onClose={() => {
                        setEditModalVisible(false);
                        setSelectedUserForEdit(null);
                        setIsCreating(false);
                    }}
                    onSave={() => {
                        fetchUsers();
                    }}
                />
                <ResetPasswordModal
                    visible={resetPasswordModalVisible}
                    user={selectedUserForResetPassword}
                    onClose={() => {
                        setResetPasswordModalVisible(false);
                        setSelectedUserForResetPassword(null);
                    }}
                    onSave={() => {
                        fetchUsers();
                    }}
                />
                <ViewPasswordModal
                    visible={viewPasswordModalVisible}
                    user={selectedUserForViewPassword}
                    onClose={() => {
                        setViewPasswordModalVisible(false);
                        setSelectedUserForViewPassword(null);
                    }}
                />
                <ActivityModal
                    visible={activityModalVisible}
                    user={selectedUserForActivity}
                    onClose={() => {
                        setActivityModalVisible(false);
                        setSelectedUserForActivity(null);
                    }}
                />
        </Container>
    );
};

export default UsersManagement;
